/**
 * Global last-line-of-defense for chunk load failures during rolling GKE
 * deploys. React error boundaries only catch errors that surface through
 * render — script-tag 404s, prefetch failures, and lazy route-chunk
 * rejections can happen outside React's path and never reach error.tsx.
 *
 * This installer runs very early (from instrumentation-client.ts) and:
 *   1. Listens for `error` events in capture phase — script/link elements
 *      don't bubble error events, so capture is required to see them.
 *   2. Listens for `unhandledrejection` — failed dynamic imports surface as
 *      rejected promises with a ChunkLoadError-shaped reason.
 *   3. Delegates to the shared time-windowed reload guard so repeated
 *      failures inside the 60s window don't spawn reload loops.
 *
 * The reload is the best-available recovery because the chunk hash in the
 * stale HTML no longer matches anything on origin. Fetching fresh HTML
 * (post-purge Cloudflare + new pods) gives the browser a consistent new
 * deployment ID and a matching chunk manifest.
 */

import {
  isRecoverableClientBoundaryError,
  recordDeploymentReloadAttempt,
  shouldAttemptDeploymentReload,
} from "./client-error-boundary";

const NEXT_CHUNK_PATH_RE = /\/_next\/static\/chunks\//;

function getChunkSrc(target: EventTarget | null): string | null {
  if (!target) return null;
  const el = target as Partial<HTMLScriptElement & HTMLLinkElement> & {
    tagName?: string;
  };
  if (!el.tagName) return null;
  if (el.tagName === "SCRIPT" && typeof el.src === "string") return el.src;
  if (el.tagName === "LINK" && typeof el.href === "string") return el.href;
  return null;
}

function triggerReload(): void {
  if (typeof window === "undefined") return;
  const pathname = window.location.pathname;
  if (!shouldAttemptDeploymentReload(pathname)) return;
  recordDeploymentReloadAttempt(pathname);
  // Defer so the current event loop tick completes (browser can still
  // flush any pending console/error reporting before navigation unwinds).
  setTimeout(() => {
    try {
      window.location.reload();
    } catch {
      // location may be blocked in extreme sandbox contexts — give up quietly.
    }
  }, 50);
}

let installed = false;

export function installChunkErrorHandler(): void {
  if (typeof window === "undefined") return;
  if (installed) return;
  installed = true;

  // Resource load errors (<script src>, <link href>) don't bubble. The only
  // way to observe them is a listener registered on window in the CAPTURE
  // phase — the third argument to addEventListener.
  window.addEventListener(
    "error",
    (event) => {
      const chunkSrc = getChunkSrc(event.target);
      if (chunkSrc && NEXT_CHUNK_PATH_RE.test(chunkSrc)) {
        triggerReload();
        return;
      }
      // Runtime-thrown ChunkLoadError (webpack/Turbopack) — event.error is the Error instance.
      if (event.error && isRecoverableClientBoundaryError(event.error)) {
        triggerReload();
      }
    },
    true
  );

  // Failed dynamic imports reject a promise; without a .catch they become
  // unhandledrejection. Rolling-deploy chunk loss is the canonical cause.
  window.addEventListener("unhandledrejection", (event) => {
    if (isRecoverableClientBoundaryError(event.reason)) {
      triggerReload();
    }
  });
}
